/**
 * useExposedWorkflows Hook
 *
 * Fetches and manages exposed workflows for the Dashboard.
 * Provides workflow selection, triggering, and state management.
 */

import { useState, useEffect, useCallback } from 'react';
import type { ExposedWorkflow, MasterTriggerInput, MasterTriggerOutput, TriggerWorkflowResponse } from '@/app/canvas/features/master-trigger/types';
import { getCSRFToken } from './useCSRF';
import { supabase } from '@/lib/supabaseClient';
export interface UseExposedWorkflowsResult {
  /** List of available exposed workflows */
  workflows: ExposedWorkflow[];

  /** Currently selected workflow (null = default chat mode) */
  selectedWorkflow: ExposedWorkflow | null;

  /** Select a workflow for message routing */
  selectWorkflow: (workflow: ExposedWorkflow | null) => void;

  /** Trigger a workflow with input */
  triggerWorkflow: (input: MasterTriggerInput) => Promise<MasterTriggerOutput | null>;

  /** Loading state for fetching workflows */
  isLoading: boolean;

  /** Loading state for workflow execution */
  isExecuting: boolean;

  /** Error message */
  error: string | null;

  /** Refresh the workflows list */
  refresh: () => Promise<void>;
}
export function useExposedWorkflows(userId: string | null): UseExposedWorkflowsResult {
  const [workflows, setWorkflows] = useState<ExposedWorkflow[]>([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState<ExposedWorkflow | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch exposed workflows from the API
   */
  const fetchWorkflows = useCallback(async () => {
    if (!userId) {
      setWorkflows([]);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/workflows/exposed?userId=${userId}`);
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch workflows');
      }
      setWorkflows(data.workflows || []);
      console.log(`[useExposedWorkflows] Loaded ${data.workflows?.length || 0} workflows`);
      if (data.workflows?.length > 0) {
        console.log('[useExposedWorkflows] Workflows:', JSON.stringify(data.workflows, null, 2));
      } else {
        console.log('[useExposedWorkflows] No exposed workflows found. To expose a workflow:');
        console.log('[useExposedWorkflows] 1. Go to Canvas page');
        console.log('[useExposedWorkflows] 2. Add a "Chatbot Trigger" node');
        console.log('[useExposedWorkflows] 3. Click "Exposed: No" to toggle it to "Exposed: Yes"');
      }
    } catch (err: any) {
      console.error('[useExposedWorkflows] Error fetching workflows:', err);
      setError(err.message || 'Failed to fetch workflows');
      setWorkflows([]);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  /**
   * Select a workflow for message routing
   */
  const selectWorkflow = useCallback((workflow: ExposedWorkflow | null) => {
    setSelectedWorkflow(workflow);
    setError(null);
    if (workflow) {
      console.log(`[useExposedWorkflows] Selected workflow: ${workflow.displayName}`);
    } else {
      console.log('[useExposedWorkflows] Cleared workflow selection (default chat mode)');
    }
  }, []);

  /**
   * Trigger the selected workflow with input
   */
  const triggerWorkflow = useCallback(async (input: MasterTriggerInput): Promise<MasterTriggerOutput | null> => {
    if (!selectedWorkflow) {
      setError('No workflow selected');
      return null;
    }
    setIsExecuting(true);
    setError(null);
    try {
      // Get CSRF token and auth token for the request
      const csrfToken = getCSRFToken();
      const {
        data: sessionData
      } = await supabase.auth.getSession();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }
      // Include auth token for internal API calls to access user's BYOK keys
      if (sessionData?.session?.access_token) {
        headers['Authorization'] = `Bearer ${sessionData.session.access_token}`;
      }
      const response_0 = await fetch('/api/workflows/trigger', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          canvasId: selectedWorkflow.canvasId,
          triggerNodeId: selectedWorkflow.triggerNodeId,
          input
        })
      });
      const data_0: TriggerWorkflowResponse = await response_0.json();
      if (!response_0.ok || !data_0.success) {
        throw new Error(data_0.error || 'Workflow execution failed');
      }
      console.log(`[useExposedWorkflows] Workflow executed successfully in ${data_0.output?.duration_ms}ms`);
      return data_0.output || null;
    } catch (err_0: any) {
      console.error('[useExposedWorkflows] Error triggering workflow:', err_0);
      setError(err_0.message || 'Workflow execution failed');
      return null;
    } finally {
      setIsExecuting(false);
    }
  }, [selectedWorkflow]);

  // Fetch workflows on mount and when userId changes
  useEffect(() => {
    fetchWorkflows();
  }, [fetchWorkflows]);
  return {
    workflows,
    selectedWorkflow,
    selectWorkflow,
    triggerWorkflow,
    isLoading,
    isExecuting,
    error,
    refresh: fetchWorkflows
  };
}